import { Grid, Typography } from "@material-ui/core";
import React from "react";
import {
    BUILDING_TYPE_KEY,
    MEASUREMENT_UNIT,
    MITIGATION_SYSTEM_KEY,
    REPORT_RESULT_COLOR,
    REPORT_RESULT_SU,
    ROOM_TYPE_KEY,
    STRUCTURE_TYPE_KEY,
    TEST_FLOOR_KEY,
    UNIT_CONVERSION_SCALE,
} from "../../../constants/values";
import ReportPageHeader from "./ReportPageHeader";
import moment from "moment";
import { isCanadaReport } from "../../../utils/reportCountry";
import ReportPageFooter from "./ReportPageFooter";

const styles = {
    sectionTitle: {
        color: "black",
        fontSize: "11px",
        fontWeight: 700,
        fontStyle: "italic",
        textAlign: "left",
    },
    sectionDivider: {
        height: "2px",
        background: "linear-gradient(to right, #3A78C9, #4A5DA0)",
        marginBottom: 5,
        marginTop: 5,
    },
    labelCol: {
        color: "#333",
        fontSize: "10px",
        fontWeight: 400,
        textAlign: "left",
        lineHeight: "1.7",
    },
    valueCol: {
        color: "#111",
        fontSize: "10px",
        fontWeight: 500,
        textAlign: "left",
        lineHeight: "1.7",
    },
    bodyText: {
        color: "#333",
        fontSize: "10px",
        fontWeight: 400,
        lineHeight: "1.6",
        paddingLeft: 20,
        paddingRight: 20,
        whiteSpace: "pre-wrap",
    },
};

const InfoRow = ({ label, value, indent = true }) => (
    <Grid container style={{ paddingLeft: indent ? 35 : 0, paddingRight: 20 }}>
        <Grid item xs={5}>
            <Typography style={styles.labelCol}>{label}</Typography>
        </Grid>
        <Grid item xs={7}>
            <Typography style={styles.valueCol}>
                {value !== undefined && value !== null && value !== "" ? value : ""}
            </Typography>
        </Grid>
    </Grid>
);

const SectionHeader = ({ title, subtitle }) => (
    <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 5 }}>
        <Typography style={styles.sectionTitle}>
            {title}
            {subtitle ? (
                <span
                    style={{
                        fontWeight: 400,
                        fontStyle: "normal",
                        fontSize: "8.5px",
                        marginLeft: 6,
                        color: "#555",
                    }}
                >
                    {subtitle}
                </span>
            ) : null}
        </Typography>
    </div>
);

const PersonBlock = ({ label, name, cert, email }) => (
    <Grid container style={{ paddingLeft: 35, paddingRight: 20, marginTop: 2 }}>
        <Grid item xs={5}>
            <Typography style={styles.labelCol}>{label}</Typography>
        </Grid>
        <Grid item xs={7}>
            <Typography style={styles.valueCol}>{name || ""}</Typography>
            <Typography style={styles.labelCol}>{cert || ""}</Typography>
            <Typography style={{ ...styles.labelCol, color: "#3A78C9" }}>
                {email || ""}
            </Typography>
        </Grid>
    </Grid>
);

const DateTimeRow = ({ label, date, time }) => (
    <Grid container style={{ paddingLeft: 35, paddingRight: 20 }}>
        <Grid item xs={5}>
            <Typography style={styles.labelCol}>{label}</Typography>
        </Grid>
        <Grid item xs={5}>
            <Typography style={styles.valueCol}>{date || ""} {time || ""}</Typography>
        </Grid>
        <Grid item xs={2}>
            <Typography style={styles.valueCol}></Typography>
        </Grid>
    </Grid>
);

/** Split a datetime string or moment-formatted string into { date, time } */
const splitDateTime = (dtString) => {
    if (!dtString) return { date: "", time: "" };
    const parts = dtString.split(",");
    if (parts.length >= 2) {
        return { date: parts[0].trim(), time: parts.slice(1).join(",").trim() };
    }
    const idx = dtString.lastIndexOf(" ");
    return { date: dtString.slice(0, idx), time: dtString.slice(idx + 1) };
};

const PageTestInfo = ({ reportDetails }) => {
    if (!reportDetails) return null;
    const isCanada = isCanadaReport(reportDetails);

    // Support both MU-web (camelCase) and EQWebDashboard (snake_case) field names
    const measurementStarted =
        reportDetails.measurementStartedLocal ??
        (reportDetails.measurement_start_d
            ? moment(reportDetails.measurement_start_d).format("MM/DD/YYYY, hh:mm A")
            : "");
    const measurementFinished =
        reportDetails.measurementFinishedLocal ??
        (reportDetails.measurement_end_d
            ? moment(reportDetails.measurement_end_d).format("MM/DD/YYYY, hh:mm A")
            : "");

    const startDt = splitDateTime(measurementStarted);
    const finishDt = splitDateTime(measurementFinished);

    // Tester info
    const fullName = reportDetails.fullName ?? reportDetails.test_by?.name;
    const certNo = reportDetails.certNo ?? reportDetails.test_by?.certNo;
    // Prefer report-display email configured in Settings over account login email.
    const email =
        reportDetails.test_by?.ownerDisplay ??
        reportDetails.owner?.ownerDisplay ??
        reportDetails.email;

    return (
        <div style={{ height: "750px", overflow: "hidden", position: "relative" }}>
            {/* ===== Shared Header ===== */}
            <ReportPageHeader reportDetails={reportDetails} />

            {/* ===== TEST INFORMATION ===== */}
            <SectionHeader title={isCanada ? "Test Information/Informations sur le test" : "Test Information"} />
            <InfoRow label={isCanada ? "Location ID/Identifiant de l'emplacement" : "Location ID"} value={reportDetails.device_name} />
            <InfoRow label={isCanada ? "Delay/Discard/Differer/Rejeter" : "Delay/Discard"} value={"24 hours"} />
            <InfoRow
                label={isCanada ? "Test Duration/Duree du test" : "Test Duration"}
                value={reportDetails.measurement_duration
                    ? reportDetails.measurement_duration
                    : "N/A"}
            />
            <InfoRow label={isCanada ? "Test Floor/Niveau teste" : "Test Floor"} value={reportDetails.device_placement ? TEST_FLOOR_KEY[reportDetails.device_placement.location_floor]
                ? TEST_FLOOR_KEY[reportDetails.device_placement.location_floor] : "N/A"
                : "N/A"} />
            <InfoRow label={isCanada ? "Room Type/Type de piece" : "Room Type"} value={reportDetails.device_placement ? ROOM_TYPE_KEY[reportDetails.device_placement.room_type]
                ? ROOM_TYPE_KEY[reportDetails.device_placement.room_type] : "N/A"
                : "N/A"} />
            <div style={{ height: 4 }} />
            <PersonBlock
                label={isCanada ? "Reported by/Signale par" : "Reported by"}
                name={fullName}
                cert={certNo ? `NRPP #${certNo}` : ""}
                email={email}
            />
            <div style={{ height: 4 }} />
            <DateTimeRow label={isCanada ? "Test Started/Debut du test" : "Test Started"} date={startDt.date} time={startDt.time} />
            <DateTimeRow label={isCanada ? "Test Finished/Fin du test" : "Test Finished"} date={finishDt.date} time={finishDt.time} />

            {/* ===== TEST LIMITATION ===== */}
            <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 10 }}>
                <Typography style={styles.sectionTitle}>{isCanada ? "Test Limitation/Limitation du test" : "Test Limitation"}</Typography>
            </div>
            <Typography style={{ ...styles.bodyText, paddingLeft: 35, paddingRight: 20, marginTop: 4, textAlign: "left" }}>
                {isCanada
                    ? "There is an uncertainty with any measurement result due to statistical variations and other factors such as daily\nand seasonal variations in radon concentrations. Variations may be due to changes in the weather,\noperation of the dwelling, or possible interference with the necessary test conditions.\nTemperature and humidity can vary depending on environmental conditions.\n\nTout resultat de mesure comporte une certaine incertitude en raison des variations statistiques et d'autres\nfacteurs, notamment les fluctuations quotidiennes et saisonnieres des concentrations de radon. Ces\nvariations peuvent etre attribuables aux changements meteorologiques, aux conditions d'utilisation du\nbatiment ou a une eventuelle perturbation des conditions requises pour la realisation du test.\nLa temperature et l'humidite peuvent varier selon les conditions environnementales."
                    : "There is an uncertainty with any measurement result due to statistical variations and other factors such as daily\nand seasonal variations in radon concentrations. Variations may be due to changes in the weather, operation of\nthe dwelling, or possible interference with the necessary test conditions.\n\nTemperature and humidity can vary depending on environmental conditions.\nThe test data was taken from a testing device approved by the National Radon Proficiency Program."}
            </Typography>

            {/* ===== COMMENTS ===== */}
            <div style={{ paddingLeft: 20, paddingRight: 20, marginTop: 10 }}>
                <Typography style={styles.sectionTitle}>{isCanada ? "Comments/Commentaires" : "Comments"}</Typography>
            </div>
            {reportDetails.device_placement?.comments ? (
                <Typography style={{ ...styles.bodyText, marginTop: 4 }}>
                    {reportDetails.device_placement?.comments}
                </Typography>
            ) : null}
            <ReportPageFooter reportDetails={reportDetails} />
        </div>
    );
};

export default PageTestInfo;
